import { PLAYER_COLORS } from "@/lib/api";
import { LAYER, boardGeom } from "@/lib/terrain";

const NATURE = "#3f7a4a";

// CONNECTIVITY v2 board links: building groups = solid player-colour links,
// nature groups = green dashed links; one bonus label per group (from the API).
// 3D BOARD: links join the centres of the squares' (lifted) top faces; coordinates in % of the
// board grid (same origin as the squares).
export default function ConnectivityOverlay({ gridCols, gridRows, edges, components, terrain = null, cellPx = 100 }) {
  const groups = Object.values(components || {}).flat();
  if (!edges?.length && !groups.length) return null;

  const geom = boardGeom(terrain, gridRows, gridCols, cellPx);
  const center = (rc) => {
    const f = geom.face(rc);
    return [((f.left + cellPx / 2) / geom.width) * 100, ((f.top + cellPx / 2) / geom.height) * 100];
  };

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: LAYER.conn }}
    >
      <svg
        data-testid="connectivity-overlay"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
      >
        {edges.map((e) => {
          const [x1, y1] = center(e.a);
          const [x2, y2] = center(e.b);
          const id = `${e.type}-${e.color}-${e.a}-${e.b}`;
          const nature = e.type === "nature";
          return (
            <g key={id}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(251,250,247,0.9)" strokeWidth={3}
                strokeLinecap="round" vectorEffect="non-scaling-stroke" />
              <line x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={nature ? NATURE : PLAYER_COLORS[e.color] || "#1E201F"}
                strokeWidth={1.6} strokeLinecap="round"
                strokeDasharray={nature ? "3 2.5" : undefined}
                vectorEffect="non-scaling-stroke"
                data-testid={`conn-edge-${id}`} />
            </g>
          );
        })}
      </svg>
      {groups.filter((g) => g.edges?.length).map((g) => (
        <GroupLabel key={`${g.type}-${g.color}-${g.squares[0]}`} group={g} center={center} />
      ))}
    </div>
  );
}

function GroupLabel({ group, center }) {
  const [a, b] = group.edges[Math.floor(group.edges.length / 2)];
  const [x1, y1] = center(a);
  const [x2, y2] = center(b);
  const building = group.type === "building";
  const bg = building ? PLAYER_COLORS[group.color] || "#7d7870" : NATURE;
  return (
    <span
      data-testid={`conn-label-${group.type}-${group.color}-${group.squares[0]}`}
      className="absolute badge-text px-1.5 py-0.5 rounded-full whitespace-nowrap"
      style={{
        left: `${(x1 + x2) / 2}%`,
        top: `${(y1 + y2) / 2}%`,
        transform: `translate(-50%, ${building ? "-110%" : "10%"})`,
        background: bg,
        color: "#fdfbf5",
        boxShadow: "0 0 0 1.5px rgba(255,255,255,.9)",
        fontSize: 10,
      }}
      title={building ? `Building group of ${group.count} stacks` : `Nature group of ${group.count} tiles`}
    >
      {group.label}
    </span>
  );
}
